import { watch } from "vue";
import { storeToRefs } from "pinia";
import { useIServiceSubCategoryNewApi } from "@/hooks/api/useIServiceApi";
import { useGetDivision } from "@/hooks/form-v3/useIServiceDivisionCode";
import { useFormV3FieldState } from "@/hooks/form-v3/useFormV3FieldState";
import { __CMP_MODEL_NUMBER__, __CMP_CATEGORY__ } from "@/const/csrCmpName";
import { __DATA_FROM_ISERVICE__ } from "@/const/iserviceConst";
import { toBool } from "@/utils/tools";

/**
 * sub-category 组件的 iService 数据源逻辑
 * 仅当 state.conf.dataFrom === __DATA_FROM_ISERVICE__ 时生效，包含：
 * 1. 加载 sub-category 选项（subNew 接口），并按 store.selectedModel.subCategory 回显选中值
 * 2. 联动逻辑：model-number 联动需开启 enableLinkage（监听 selectedModel）；
 *    其余场景默认跟 category 字段的值联动（iService 数据源的 category value 为 englishName，
 *    需从其 dataSource 反查 categoryId 后再请求）
 * @param {Object} options
 * @param {Object} options.store - csr pinia store，读取 selectedModel
 * @param {Object} options.state - 当前组件的 csr state（含 conf.dataFrom/conf.serviceType/conf.parentFieldName/conf.enableLinkage）
 * @param {Object} options.currentActionState - 当前表单提交按钮的 state（用于获取 divisionCode）
 */
export const useIServiceSubCategory = ({ store, state, currentActionState }) => {
  if (state.conf?.dataFrom !== __DATA_FROM_ISERVICE__) {
    return;
  }

  const parentFieldName = state.conf?.parentFieldName;
  const isModelNumberLinkage = parentFieldName === __CMP_MODEL_NUMBER__;

  // 加载sub-category选项（仅在开启联动时由 watch 调用）
  const loadIServiceSubCategory = async (categoryId) => {
    // 父级未选择时清空选项；仅 model-number 联动需禁用，其他场景不设置 disabled
    if (!categoryId) {
      state.conf = {
        ...state.conf,
        dataSource: [],
        ...(isModelNumberLinkage ? { disabled: true } : {}),
      };
      state.value = null;
      return;
    }
    const { divisionCode } = await useGetDivision(currentActionState);
    const { data } = await useIServiceSubCategoryNewApi({
      divisionCode,
      categoryId: categoryId || '',
      serviceType: state.conf.serviceType,
    });
    const subCategories = (data.value || []).map(item => ({
      label: item.englishName,
      value: item.englishName,
      ...item,
    }));
    state.conf = {
      ...state.conf,
      dataSource: subCategories,
      ...(isModelNumberLinkage ? { disabled: true } : {}),
    };
    // 根据选中suggestion的subCategory匹配并赋值
    const selectedSubCategory = store.selectedModel?.subCategory;
    if (selectedSubCategory) {
      const matchedItem = subCategories.find(item => item.categoryId === selectedSubCategory);
      if (matchedItem) {
        state.value = matchedItem.value;
      }
    }
  };

  // 联动逻辑：model-number 联动需开启 enableLinkage；其余场景（dataFrom 为 iservice）默认跟 category 联动
  if (toBool(state.conf?.enableLinkage) && isModelNumberLinkage) {
    // 监听选中的suggestion对象，取其buId作为sub-category的父级categoryId
    const selectedModel = storeToRefs(store).selectedModel;
    watch(selectedModel, (model) => {
      state.value = null;
      loadIServiceSubCategory(model?.buId);
    }, { immediate: true });
  } else {
    const categoryState = useFormV3FieldState(state.el, __CMP_CATEGORY__);
    // 取category当前值对应的categoryId：
    // iService数据源的value为englishName，需从dataSource反查（未加载完成时返回null，避免错误请求）；
    // redemption等其他数据源的value本身就是categoryId，直接使用
    const getCategoryId = () => {
      const { value, conf } = categoryState || {};
      if (!value) {
        return null;
      }
      if (conf?.dataFrom !== __DATA_FROM_ISERVICE__) {
        return value;
      }
      return conf?.dataSource?.find(item => item.value === value)?.categoryId || null;
    };
    // dataSource为异步加载，需连同value一起监听，加载完成后才能触发联动（仅在联动时查找一次，无性能问题）
    watch([() => categoryState?.value, () => categoryState?.conf?.dataSource], () => {
      state.value = null;
      loadIServiceSubCategory(getCategoryId());
    }, { immediate: true });
  }
};
